/**
 * Shrink a raster image file before upload. Brand logos and map marker icons
 * render at 36–40 px, yet uploads were stored at camera size — the staging
 * org logo was a 419 KB PNG that every page (favicon + sidebar) downloaded
 * (2026-10-26). SVGs and GIFs pass through untouched (vector / animated),
 * as does anything already within the limit, and any decode/encode failure
 * returns the original file so an upload can never be blocked by this step.
 */
const PASSTHROUGH_TYPES = new Set(['image/svg+xml', 'image/gif']);

export function needsDownscale(file, { maxDim, maxBytes }) {
  if (!file || !file.type?.startsWith('image/')) return false;
  if (PASSTHROUGH_TYPES.has(file.type)) return false;
  return typeof maxBytes === 'number' && file.size > maxBytes ? true : null; // null = decide by dimensions
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode failed')); };
    img.src = url;
  });
}

export async function downscaleImageFile(file, { maxDim = 512, maxBytes = 64 * 1024 } = {}) {
  const bySize = needsDownscale(file, { maxDim, maxBytes });
  if (bySize === false) return file;
  try {
    const img = await loadImage(file);
    const longest = Math.max(img.width, img.height);
    if (!bySize && longest <= maxDim) return file;
    const scale = Math.min(1, maxDim / longest);
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob || blob.size >= file.size) return file;
    const name = file.name.replace(/\.[^.]+$/, '') + '.png';
    return new File([blob], name, { type: 'image/png' });
  } catch {
    return file;
  }
}
